/* Paid collection pairs: Shopify owns the discount. No gift markers, automatic
   additions or removals. Confirm the difference between complete cart quotes. */
(() => {
  if (window.K9NativeBogo) return;
  const identity = item => JSON.stringify([Number(item.id), Object.entries(item.properties || {})
    .filter(([, value]) => value !== '').sort(([a], [b]) => a.localeCompare(b))]);
  const merge = items => {
    const lines = new Map();
    for (const item of items) {
      if (!Number.isSafeInteger(item.id) || item.id < 1 || !Number.isSafeInteger(item.quantity)
        || item.quantity < 1 || item.selling_plan_allocation) throw Error('line');
      const key = identity(item), line = lines.get(key) || { ...item, quantity: 0 };
      line.quantity += item.quantity; lines.set(key, line);
    }
    if (lines.size > 250) throw Error('too-many-lines');
    return [...lines.values()];
  };
  const quantities = items => new Map(merge(items).map(item => [identity(item), item.quantity]));
  const money = (value, currency) => {
    if (value?.currencyCode !== currency || !/^\d+(?:\.\d{1,2})?$/.test(value.amount)) throw Error('currency');
    const cents = Math.round(Number(value.amount) * 100);
    if (!Number.isSafeInteger(cents)) throw Error('money');
    return cents;
  };
  const isCollectionUpsell = config => config.kind === 'collection-upsell';
  const isUpsell = config => config.kind === 'product-upsell' || isCollectionUpsell(config);
  // Lot 6b-1 (owner, 27 Oct.): nothing hard-coded; the offer type gives the kind, the Shopify quote gives the rate.
  const isShocksafe = config => config.kind === 'product-upsell';
  const triggerVariant = (config, id) => config.triggerVariants?.find(v => String(v.id) === String(id));
  const shocksafeColorKey = value => {
    const text = String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    for (const [key, pattern] of [
      ['bleu-ciel', /^(bleu ciel|sky blue)$/], ['noir', /^(noir(?: ebene)?|black)$/],
      ['kaki', /^(khaki|kaki(?: militaire)?|marron|brown|brun fauve)$/], ['vert', /^(vert(?: foret| commando)?|green)$/],
      ['rouge', /^(rouge|red)$/], ['orange', /^orange$/], ['rose', /^(rose|pink)$/],
      ['bleu', /^(bleu|blue)$/], ['violet', /^(violet|purple)$/]
    ]) if (pattern.test(text)) return key;
    return text;
  };
  const matchedShocksafeVariant = (config, cart, chosen) => {
    if (!isShocksafe(config)) return null;
    // Carrier page: the carrier variant chosen in the offer; drawer: the first carrier line.
    const first = chosen != null ? { id: Number(chosen) }
      : cart.items.find(item => item.product_id === config.triggerProductId && config.triggerVariantIds.includes(item.id));
    const color = first && config.triggerColors?.find(variant => variant.id === first.id)?.color;
    // Carrier page: without an equivalent colour, keep the leash rendered by Liquid (suggested variant).
    const fallback = chosen != null ? null : config.catalog.find(variant => variant.available) || null;
    if (!color) return fallback;
    const key = shocksafeColorKey(color);
    return config.catalog.find(variant => variant.available && variant.color && shocksafeColorKey(variant.color) === key) || fallback;
  };
  const ordinary = item => !item.properties?._k9_cadeau && !item.has_components && !item.item_components?.length;
  const qualifying = (config, item) => ordinary(item) && config.triggerCatalog.some(v => v.id === item.id
    && (item.product_id === undefined || item.product_id === v.productId));
  const qualifyingCount = (config, input) => input.filter(item => qualifying(config, item))
    .reduce((sum, item) => sum + item.quantity, 0);
  const positive = value => Number.isSafeInteger(value) && value >= 1;
  // Shape only (lot 6b-1): handles, products, rates and titles are data of the offer, never constants.
  const validateRule = config => {
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(config.handle) || Object.hasOwn(config, 'percentage') || Object.hasOwn(config, 'discountTitle')
      || !Array.isArray(config.catalog) || !config.catalog.length || config.catalog.length > 250) throw Error('rule');
    // Both upsells: one proposed product, one discounted unit.
    if (isUpsell(config) && (config.maxDiscountedQuantity !== 1
      || new Set(config.catalog.map(v => v.productId)).size !== 1)) throw Error('rule');
    if (isCollectionUpsell(config)) {
      if (!Array.isArray(config.triggerCatalog) || !config.triggerCatalog.length || config.triggerCatalog.length > 250
        || new Set(config.triggerCatalog.map(v => v.productId)).size > 50
        || config.triggerCatalog.some(v => config.triggerCatalog.filter(other => other.productId === v.productId).length > 50)
        || new Set(config.triggerCatalog.map(v => v.id)).size !== config.triggerCatalog.length
        || config.triggerCatalog.some(v => !positive(v.id) || !positive(v.productId)
          // The reward may trigger the offer itself (a wheel alone), only as the same product.
          || config.catalog.some(reward => reward.id === v.id && reward.productId !== v.productId))) throw Error('rule');
    } else if (config.kind === 'product-upsell') {
      if (!positive(config.triggerProductId) || config.catalog.some(v => v.productId === config.triggerProductId)
        || !Array.isArray(config.triggerVariantIds) || !config.triggerVariantIds.length
        || config.triggerVariantIds.length > 250
        || new Set(config.triggerVariantIds).size !== config.triggerVariantIds.length
        || config.triggerVariantIds.some(id => !positive(id) || config.catalog.some(v => v.id === id))) throw Error('rule');
      // Carrier page only: the carrier catalogue with its current prices, exactly the trigger variants.
      if (config.triggerVariants !== undefined && (!Array.isArray(config.triggerVariants)
        || config.triggerVariants.length !== config.triggerVariantIds.length
        || config.triggerVariants.some(v => !config.triggerVariantIds.includes(v.id) || v.productId !== config.triggerProductId
          || !Number.isSafeInteger(v.price) || v.price < 1 || typeof v.available !== 'boolean')
        || new Set(config.triggerVariants.map(v => v.id)).size !== config.triggerVariants.length)) throw Error('rule');
    } else if (config.kind !== 'pair' || !positive(config.collectionId)) throw Error('rule');
    if (new Set(config.catalog.map(v => v.id)).size !== config.catalog.length
      || config.catalog.some(v => !Number.isSafeInteger(v.id) || v.id < 1
        || !Number.isSafeInteger(v.productId) || v.productId < 1
        || !Number.isSafeInteger(v.price) || v.price < 1)) throw Error('catalog');
    return config;
  };
  const count = (cart, config) => cart.items.filter(item => config.catalog.some(v => v.productId === item.product_id))
    .reduce((sum, item) => sum + item.quantity, 0);
  const items = (config, mode, choices) => {
    if (isShocksafe(config) && mode === 'bundle') {
      const carrier = triggerVariant(config, choices.primary);
      const leash = config.catalog.find(v => String(v.id) === String(choices.secondary) && v.available);
      return carrier?.available && leash ? [{ id: carrier.id, quantity: 1 }, { id: leash.id, quantity: 1 }] : null;
    }
    if (isUpsell(config) && mode !== 'cart') return null;
    const keys = mode === 'cart' ? ['secondary'] : ['primary', 'secondary'];
    const selected = keys.map(key => config.catalog.find(v => String(v.id) === String(choices[key]) && v.available));
    return selected.some(v => !v) ? null : merge(selected.map(v => ({ id: v.id, quantity: 1 })));
  };
  const validateCart = (cart, currency) => {
    if (!Array.isArray(cart?.items) || cart.currency !== currency
      || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0
      || cart.items.some(i => !Number.isSafeInteger(i.product_id) || i.product_id < 1 || !i.key)) throw Error('cart');
    merge(cart.items); return cart;
  };
  const quoteLines = input => merge(input).map(item => ({ merchandiseId: `gid://shopify/ProductVariant/${item.id}`,
    quantity: item.quantity, attributes: Object.entries(item.properties || {}).filter(([, v]) => v !== '')
      .map(([key, value]) => ({ key, value: String(value) })) }));
  const codes = cart => (cart.discount_codes || []).filter(code => code.applicable).map(code => code.code);
  const sorted = values => values.sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
  const entries = value => Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b));
  const allocations = item => sorted((item.line_level_discount_allocations || []).map(a =>
    [a.amount, entries(a.discount_application)])); // A zero allocation still reserves a promotion.
  const fragment = item => [identity(item), item.key, item.product_id, item.quantity,
    item.original_price, item.final_price, item.original_line_price, item.final_line_price,
    allocations(item), sorted((item.discounts || []).map(entries)), Boolean(item.has_components),
    sorted((item.item_components || []).map(fragment))];
  const cartFingerprint = (cart, includeAttributes) => JSON.stringify([cart.currency, cart.total_price,
    sorted(cart.items.map(fragment)), sorted((cart.discount_codes || []).map(entries)),
    sorted((cart.cart_level_discount_applications || []).map(entries)),
    includeAttributes ? entries(cart.attributes) : [], cart.note || '']);
  const fingerprint = cart => cartFingerprint(cart, true);
  // Only displayed quote -> fresh reference tolerates late server cart attributes.
  // The reference -> final read comparison remains complete before the POST.
  const displayFingerprint = cart => cartFingerprint(cart, false);
  // Bridge the two Shopify APIs without reducing differently priced fragments to an average.
  // Keys and discount application timestamps are transport details, not financial evidence.
  const cents = value => {
    if (!Number.isSafeInteger(value) || value < 0) throw Error('financial-price');
    return value;
  };
  const componentShape = line => {
    const components = new Map();
    for (const c of line.item_components || []) {
      if (!Number.isSafeInteger(c.id) || c.id < 1 || !Number.isSafeInteger(c.quantity) || c.quantity < 1) throw Error('financial-components');
      components.set(c.id, (components.get(c.id) || 0) + c.quantity);
    }
    if (Boolean(line.has_components) !== Boolean(components.size) || components.size && line.quantity !== 1) throw Error('financial-components');
    return [...components].sort(([a], [b]) => a - b);
  };
  const financialLines = (input, config, rules = []) => {
    const stock = new Map(), bands = new Map(), ownBands = new Map();
    const add = (map, key, quantity) => { key = JSON.stringify(key); map.set(key, (map.get(key) || 0) + quantity); };
    for (const line of input) {
      const q = line.quantity;
      if (!Number.isSafeInteger(q) || q < 1 || !Number.isSafeInteger(line.id) || line.id < 1
        || !Number.isSafeInteger(line.product_id) || line.product_id < 1
        || line.selling_plan_allocation || line.parent_relationship) throw Error('financial-line');
      const original = cents(line.original_line_price), final = cents(line.final_line_price);
      const unit = value => {
        if (!Number.isSafeInteger(value / q)) throw Error('financial-fragment');
        return value / q;
      };
      if (line.original_price !== undefined && line.original_price !== unit(original)
        || line.final_price !== undefined && line.final_price !== unit(final)) throw Error('financial-fragment');
      // Multiple-parent component quantities have not been bridged between Ajax and
      // Storefront. Refuse rather than infer whether those quantities are relative.
      const children = componentShape(line);
      const properties = { ...line.properties }, handle = properties._k9_cadeau;
      const gift = rules.find(r => r.products.secondary.id === line.product_id
        && r.products.secondary.variants.some(v => v.id === line.id));
      if (handle && (!gift || gift.handle !== handle)) throw Error('financial-gift');
      if (gift) delete properties._k9_cadeau;
      const grouped = identity({ ...line, properties });
      const eligible = ordinary(line) && !gift && config.catalog.some(v => v.id === line.id && v.productId === line.product_id);
      let discount = 0, own = 0, rate = null;
      const foreign = [];
      for (const a of line.line_level_discount_allocations || []) {
        const app = a.discount_application, amount = cents(a.amount);
        if (!app?.title || !['', 'automatic', 'discount_code'].includes(app.type)
          || app.target_type && app.target_type !== 'line_item') throw Error('financial-allocation');
        const kind = app.type === 'discount_code' ? 'code' : 'automatic';
        discount += amount;
        // Zero allocations are ignored on both sides: /cart.js and a quote need not list the same ones.
        if (!amount) continue;
        // Ours: an automatic percentage on a catalogue line ('20.0' in /cart.js, 20 in a quote), whatever its title.
        const percentage = app.value_type === 'percentage' ? Number(app.value) : NaN;
        if (eligible && kind === 'automatic' && Number.isInteger(percentage) && percentage >= 1 && percentage <= 99) {
          if (rate !== null) throw Error('financial-allocation');
          own = amount; rate = percentage;
        }
        else foreign.push([kind, app.title, unit(amount)]);
      }
      if (discount + final !== original) throw Error('financial-allocation');
      add(stock, [identity(line), line.product_id, unit(original), children], q);
      add(bands, [grouped, line.product_id, unit(original), unit(final + own), sorted(foreign), children], q);
      // Only our automatic allocation may move between equally priced, otherwise identical
      // eligible choices, at the same rate. Other promotions remain tied to their exact variant and price band.
      if (eligible) add(ownBands, [entries(properties), unit(original), unit(final + own), sorted(foreign), rate, unit(own)], q);
    }
    const list = map => sorted([...map]);
    return { stock: list(stock), bands: list(bands), own: list(ownBands) };
  };
  const financial = (cart, config, rules) => {
    // Order-level discounts need a separate bridge; line allocations alone cannot certify them.
    if (cart.currency !== config.currency || cart.cart_level_discount_applications?.length) throw Error('financial-cart');
    const total = cents(cart.total_price);
    if (cart.items.reduce((sum, i) => sum + cents(i.final_line_price), 0) !== total) throw Error('financial-total');
    return JSON.stringify([total, financialLines(cart.items, config, rules)]);
  };
  const quotedLine = (line, currency) => ({ id: Number(line.merchandise.id.split('/').pop()),
    product_id: Number(line.merchandise.product.id.split('/').pop()), quantity: line.quantity,
    properties: Object.fromEntries(line.attributes.map(a => [a.key, a.value])),
    original_line_price: money(line.cost.subtotalAmount, currency), final_line_price: money(line.cost.totalAmount, currency),
    has_components: Boolean(line.lineComponents?.length), item_components: (line.lineComponents || []).map(c => ({
      id: Number(c.merchandise.id.split('/').pop()), quantity: c.quantity })),
    line_level_discount_allocations: line.discountAllocations.map(a => {
      const type = { CartAutomaticDiscountAllocation: 'automatic', CartCodeDiscountAllocation: 'discount_code' }[a.__typename];
      if (!type) throw Error('financial-allocation');
      const value = a.discountApplication?.value, p = value?.percentage;
      // Same shape as /cart.js: value_type and value. A float within 1e-9 of an integer (0.29 × 100) is that
      // integer, as in rateOf: otherwise the « ours » band of the quote would differ from the one of /cart.js.
      const applied = value?.__typename === 'PricingPercentageValue'
        ? { value_type: 'percentage', value: typeof p === 'number' && Math.abs(p - Math.round(p)) < 1e-9 ? Math.round(p) : p }
        : value?.__typename === 'MoneyV2' ? { value_type: 'fixed_amount', value: value.amount } : {};
      return { amount: money(a.discountedAmount, currency), discount_application: { type, title: type === 'automatic' ? a.title : a.code, ...applied } };
    }) });
  const confirmed = (before, after, added, quote, config, rules = []) => {
    try {
      const expected = quantities([...before.items, ...added]), actual = quantities(after.items);
      return after.currency === before.currency && after.total_price === quote.total
        && actual.size === expected.size && [...expected].every(([key, qty]) => actual.get(key) === qty)
        && JSON.stringify(entries(after.attributes)) === JSON.stringify(entries(before.attributes))
        && (after.note || '') === (before.note || '')
        && JSON.stringify(sorted((after.discount_codes || []).map(entries))) === JSON.stringify(sorted((before.discount_codes || []).map(entries)))
        && financial(after, config, rules) === quote.financial;
    } catch { return false; }
  };
  // Unit prices Shopify must discount in this cart, without any rate (lot 6b-1).
  const discountedUnits = (config, input) => {
    if (isCollectionUpsell(config)) {
      const units = qualifyingCount(config, input);
      const rewards = input.filter(ordinary).flatMap(item => {
        const variant = config.catalog.find(v => v.id === item.id && (item.product_id === undefined || item.product_id === v.productId));
        // The unit that triggers the offer is never its own reward.
        return variant && units - Number(qualifying(config, item)) >= 1 ? [variant.price] : [];
      });
      return rewards.length ? [Math.min(...rewards)] : [];
    }
    if (isUpsell(config) && !input.some(item => config.triggerVariantIds.includes(item.id))) return [];
    const prices = [];
    for (const item of merge(input)) {
      const variant = config.catalog.find(v => v.id === item.id);
      if (variant) for (let n = 0; n < item.quantity; n++) prices.push(variant.price);
    }
    prices.sort((a, b) => a - b);
    return prices.slice(0, isUpsell(config) ? 1 : Math.floor(prices.length / 2));
  };
  // Shopify rounds each discounted unit: Σ round(price × p / 100).
  const saving = (units, p) => units.reduce((sum, price) => sum + Math.round(price * p / 100), 0);
  // Automatic allocations of one quoted catalogue line, grouped by (title, value); zero groups are ignored.
  // Codes are never ours: the exact price check refuses them. One discount per line, otherwise « stacked ».
  const ownAllocations = line => {
    const groups = new Map(), currency = line.cost?.subtotalAmount?.currencyCode;
    for (const allocation of line.discountAllocations || []) {
      if (allocation.__typename !== 'CartAutomaticDiscountAllocation') continue;
      const key = JSON.stringify([allocation.title ?? '', allocation.discountApplication?.value ?? null]);
      const group = groups.get(key) || { allocation, amount: 0 };
      group.amount += money(allocation.discountedAmount, currency); groups.set(key, group);
    }
    const own = [...groups.values()].filter(group => group.amount > 0);
    if (own.length > 1) throw Error('stacked');
    return own.map(({ allocation, amount }) => ({ title: allocation.title ?? '', rate: window.K9NativeOffer.rateOf(allocation), amount }));
  };
  // Reason of a hidden card (data-k9-raison): the engine error code, never a free message.
  const reason = error => /^[a-z0-9-]+$/.test(error?.message || '') ? error.message : 'erreur';
  const api = { identity, merge, quantities, money, validateRule, validateCart, count, items, quoteLines, fingerprint, displayFingerprint, financial, quotedLine, confirmed, discountedUnits, saving, ownAllocations, shocksafeColorKey, matchedShocksafeVariant };
  window.K9NativeBogo = api;
  function register() {
    if (!window.K9NativeOffer || customElements.get('k9-bogo-offer')) return;
    const { Base, state } = window.K9NativeOffer;
    const offers = () => [...document.querySelectorAll('k9-patch-offer, k9-product-offer, k9-fixed-offer, k9-gift-offer, k9-bogo-offer')].filter(e => e.config);
    const refresh = () => offers().forEach(e => e.update());
    const query = `mutation K9PairQuote($input: CartInput!, $country: CountryCode!) @inContext(country: $country) {
      cartCreate(input: $input) { cart { id
        discountCodes { code applicable }
        cost { totalAmount { amount currencyCode } }
        lines(first: 250) { nodes {
          id quantity attributes { key value } merchandise { ... on ProductVariant { id product { id } } }
          cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }
          ... on ComponentizableCartLine { lineComponents {
            quantity merchandise { ... on ProductVariant { id } }
          } }
          discountAllocations { __typename discountedAmount { amount currencyCode }
            discountApplication { targetType value { __typename
              ... on PricingPercentageValue { percentage } ... on MoneyV2 { amount currencyCode } } }
            ... on CartAutomaticDiscountAllocation { title } ... on CartCodeDiscountAllocation { code } }
        } pageInfo { hasNextPage } }
      } userErrors { code } warnings { code } }
    }`;
    const clearQuery = `mutation K9PairClear($cart: ID!, $lines: [ID!]!, $country: CountryCode!) @inContext(country: $country) {
      cartLinesRemove(cartId: $cart, lineIds: $lines) { cart { totalQuantity } userErrors { code } }
    }`;
    let quoteQueue = Promise.resolve(), quoteUncertain = false;
    const pendingQuotes = new Map();
    class BogoOffer extends Base {
      connectedCallback() {
        this.events?.abort(); this.events = new AbortController();
        this.preparationActive = false;
        this.generation = (this.generation || 0) + 1; this.selection = ''; this.verified = null;
        try { this.config = validateRule(JSON.parse(this.querySelector('[data-offer-config]').textContent)); }
        catch (error) { this.dataset.k9Devis = 'echec'; this.dataset.k9Raison = reason(error); this.hidden = true; return; }
        this.mode = this.dataset.mode;
        this.key = `${this.config.handle}:${this.mode}:${this.dataset.pageProduct || ''}`;
        this.draft = state.drafts.get(this.key) || { choices: {}, open: true, blocked: false };
        state.drafts.set(this.key, this.draft);
        this.button = this.querySelector('[data-submit]'); this.label = this.querySelector('[data-button-label]');
        this.defaultLabel = this.label.textContent;
        if (isShocksafe(this.config)) this.defaultAriaLabel = this.button.getAttribute('aria-label');
        for (const select of this.querySelectorAll('[data-variant]')) {
          const saved = this.draft.choices[select.dataset.variant];
          if (saved && [...select.options].some(o => o.value === saved)) select.value = saved;
        }
        const options = { signal: this.events.signal };
        this.addEventListener('change', event => {
          event.stopPropagation();
          if (isShocksafe(this.config) && event.target.matches?.('[data-variant="secondary"]')) this.draft.shocksafeExplicit = true;
          this.update();
        }, options);
        this.addEventListener('input', event => event.stopPropagation(), options);
        this.button.addEventListener('click', () => this.submit(), options);
        if (isShocksafe(this.config)) this.connectColors(options);
        if (isShocksafe(this.config) && this.mode === 'bundle') {
          this.watchDrawer(document.querySelector('cart-drawer'), options);
          window.addEventListener('gp:product-change-variant', event => this.followMainVariant(event), options);
        }
        this.update();
        const activate = () => { this.preparationActive = true; this.selection = ''; this.update(); };
        if (window.K9OfferActivation) window.K9OfferActivation(this, activate); else activate();
      }
      disconnectedCallback() {
        this.events?.abort(); this.colorResize?.disconnect(); this.colorEdges?.disconnect(); this.generation++;
        if (this.colorFrame != null) cancelAnimationFrame(this.colorFrame);
        this.colorFrame = null;
      }
      // Carrier page: an add through the main button or a removal in the drawer changes eligibility.
      watchDrawer(drawer, options) {
        if (!drawer) return;
        const requote = async () => {
          // Never hide a pending purchase or its uncertain message; never re-quote an unchanged cart.
          if (state.pending || this.draft.blocked) return;
          if (this.verified) {
            try {
              const current = await this.readCart();
              if (current.response.ok && fingerprint(validateCart(current.data, this.config.currency)) === this.verified.fingerprint) return;
            } catch { /* A failed read re-quotes, which hides the offer if the cart stays unreadable. */ }
          }
          if (state.pending || this.draft.blocked) return;
          this.selection = ''; this.update();
        };
        drawer.addEventListener('k9:drawer-open', requote, options);
        drawer.addEventListener('k9:drawer-close', requote, options);
      }
      // Carrier page: the carrier variant follows the main product form (size, colour) once chosen there.
      followMainVariant(event) {
        if (String(event.detail?.productId) !== String(this.config.triggerProductId)) return;
        const id = event.detail?.variantSelected?.id, select = this.querySelector('[data-variant="primary"]');
        if (id == null || !select || select.value === String(id)
          || ![...select.options].some(option => option.value === String(id) && !option.disabled)) return;
        select.value = String(id);
        this.update();
      }
      connectColors(options) {
        const select = this.querySelector('[data-variant="secondary"]'), rail = this.querySelector('[data-color-rail]');
        if (!select || !rail) return;
        this.querySelector('[data-shocksafe-select]').hidden = true;
        this.querySelector('[data-shocksafe-colors]').hidden = false;
        this.addEventListener('click', event => {
          const button = event.target.closest?.('[data-color-variant], [data-color-scroll]');
          if (!button || !this.contains(button) || button.disabled || state.pending) return;
          event.preventDefault(); event.stopPropagation();
          if (button.dataset.colorVariant) {
            const variant = this.config.catalog.find(v => String(v.id) === button.dataset.colorVariant && v.available);
            if (!variant) return;
            this.draft.shocksafeExplicit = true;
            select.value = String(variant.id);
            this.update();
          } else {
            const direction = Number(button.dataset.colorScroll);
            if (direction !== -1 && direction !== 1) return;
            rail.scrollBy({ left: direction * Math.max(44, rail.clientWidth * .75),
              behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
          }
        }, options);
        this.colorSelection = undefined;
        // Edge states follow the first and last swatch visibility; no geometry is read while scrolling.
        this.railEdges = { start: true, end: true };
        const swatches = this.querySelectorAll('[data-color-variant]');
        if (swatches.length && typeof IntersectionObserver !== 'undefined') {
          const first = swatches[0], last = swatches[swatches.length - 1];
          this.colorEdges?.disconnect();
          this.colorEdges = new IntersectionObserver(entries => {
            for (const entry of entries) {
              const visible = entry.isIntersecting && entry.intersectionRatio >= .95;
              if (entry.target === first) this.railEdges.start = visible;
              if (entry.target === last) this.railEdges.end = visible;
            }
            this.reflectRailEdges();
          }, { root: rail, threshold: [0, .95, 1] });
          this.colorEdges.observe(first); if (last !== first) this.colorEdges.observe(last);
        }
        if (typeof ResizeObserver !== 'undefined') {
          this.colorResize?.disconnect();
          this.colorResize = new ResizeObserver(() => this.scheduleColorRail());
          this.colorResize.observe(rail);
        }
      }
      reflectColors() {
        if (!isShocksafe(this.config)) return;
        const selected = this.config.catalog.find(v => String(v.id) === this.draft.choices.secondary);
        const name = this.querySelector('[data-color-name]');
        if (name && name.textContent !== (selected?.color || '')) name.textContent = selected?.color || '';
        for (const button of this.querySelectorAll('[data-color-variant]')) {
          const variant = this.config.catalog.find(v => String(v.id) === button.dataset.colorVariant);
          const pressed = String(String(variant?.id) === this.draft.choices.secondary);
          if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
          const disabled = state.pending || !variant?.available;
          if (button.disabled !== disabled) button.disabled = disabled;
        }
        this.reflectRailEdges();
        this.scheduleColorRail();
      }
      reflectRailEdges() {
        const rail = this.querySelector('[data-color-rail]'), edges = this.railEdges;
        if (!rail || !edges) return;
        rail.toggleAttribute?.('data-more-start', !edges.start);
        rail.toggleAttribute?.('data-more-end', !edges.end);
        for (const arrow of this.querySelectorAll('[data-color-scroll]')) {
          const disabled = state.pending || (Number(arrow.dataset.colorScroll) < 0 ? edges.start : edges.end);
          if (arrow.disabled !== disabled) arrow.disabled = disabled;
          if (arrow.getAttribute('aria-disabled') !== String(disabled)) arrow.setAttribute('aria-disabled', String(disabled));
        }
      }
      scheduleColorRail() {
        if (this.colorFrame != null) return;
        this.colorFrame = requestAnimationFrame(() => {
          this.colorFrame = null;
          if (this.isConnected) this.syncColorRail();
        });
      }
      syncColorRail() {
        const rail = this.querySelector('[data-color-rail]');
        if (!rail || this.hidden) return;
        // Read geometry once after the drawer render, only to reveal the selected swatch.
        const width = rail.clientWidth;
        if (!width) return;
        const maximum = Math.max(0, rail.scrollWidth - width);
        const currentLeft = rail.scrollLeft;
        let left = currentLeft;
        if (this.colorSelection !== this.draft.choices.secondary) {
          const button = this.querySelector(`[data-color-variant="${this.draft.choices.secondary}"]`);
          if (button) {
            const start = button.getBoundingClientRect().left - rail.getBoundingClientRect().left + left;
            const end = start + button.offsetWidth;
            if (start < left) left = start;
            else if (end > left + width) left = end - width;
            this.colorSelection = this.draft.choices.secondary;
          }
        }
        if (left !== currentLeft) {
          left = Math.max(0, Math.min(maximum, left));
          rail.scrollLeft = left;
        }
      }
      matchHarnessColor(cart) {
        if (!isShocksafe(this.config) || this.draft.shocksafeExplicit) return false;
        const chosen = this.mode === 'bundle' ? this.draft.choices.primary : undefined;
        const variant = matchedShocksafeVariant(this.config, cart, chosen), select = this.querySelector('[data-variant="secondary"]');
        if (!variant || !select || select.value === String(variant.id)
          || ![...select.options].some(option => option.value === String(variant.id) && !option.disabled)) return false;
        select.value = String(variant.id);
        return true;
      }
      message(text, recovery = false) {
        if (isShocksafe(this.config) && this.mode !== 'bundle') this.querySelector('[data-status]')?.classList.toggle('visually-hidden', Boolean(text) && text === this.config.text.added);
        super.message(text, recovery);
      }
      update() {
        for (const select of this.querySelectorAll('[data-variant]')) this.draft.choices[select.dataset.variant] = select.value;
        for (const photo of this.querySelectorAll('[data-product-image]')) {
          const choice = this.draft.choices[photo.dataset.productImage];
          const v = this.config.catalog.find(v => String(v.id) === String(choice))
            || (photo.dataset.productImage === 'primary' ? triggerVariant(this.config, choice) : null);
          const image = photo.querySelector('img');
          if (v?.image && image) { image.removeAttribute('srcset'); image.src = v.image; image.alt = v.title; }
        }
        const title = this.querySelector('[data-selected-title]');
        if (title) title.textContent = this.config.catalog.find(v => String(v.id) === String(this.draft.choices.secondary))?.title || '';
        const selection = JSON.stringify(this.draft.choices);
        if (selection !== this.selection) {
          this.selection = selection; this.verified = null;
          if (this.preparationActive !== false) void this.refreshQuote();
        }
        this.button.disabled = state.pending || !this.verified || this.draft.blocked || Boolean(window.K9NativeGift?.checkoutBlocked);
        this.button.setAttribute('aria-disabled', String(this.button.disabled));
        const label = this.addedShown ? this.config.text.addedButton : state.pending ? this.config.text.busy : this.defaultLabel;
        this.label.textContent = label;
        if (isShocksafe(this.config)) {
          // The carrier page button has no accessible name override; never write a literal « null ».
          const name = this.addedShown || state.pending ? label : this.defaultAriaLabel;
          if (name) this.button.setAttribute('aria-label', name); else this.button.removeAttribute('aria-label');
        }
        for (const select of this.querySelectorAll('select')) select.disabled = state.pending;
        this.reflectColors();
      }
      async readCart() {
        const url = new URL(this.config.cartUrl, location.origin);
        if (url.origin !== location.origin || !url.pathname.endsWith('/cart.js') || url.search || url.hash) throw Error('endpoint');
        const initial = await Base.json(this.config.cartUrl);
        if (!initial.response.ok) throw Error('cart');
        const raw = validateCart(initial.data, this.config.currency);
        if (!raw.items.some(i => i.has_components && !i.item_components?.length)) return initial;
        url.pathname = url.pathname.slice(0, -3); url.search = '?view=k9-volume';
        const result = await Base.json(url.href), reader = result.data;
        if (!result.response.ok || reader?.version !== 1 || reader.currency !== raw.currency
          || reader.total_price !== raw.total_price || !Array.isArray(reader.items) || reader.items.length !== raw.items.length
          || new Set(reader.items.map(i => i.key)).size !== reader.items.length
          || new Set(raw.items.map(i => i.key)).size !== raw.items.length) throw Error('components-reader');
        const expanded = { ...raw, raw, items: raw.items.map(item => {
          const line = reader.items.find(i => i.key === item.key && i.id === item.id && i.quantity === item.quantity);
          if (!Array.isArray(line?.components)) throw Error('components-reader');
          const current = { ...item, item_components: line.components }; componentShape(current); return current;
        }) };
        const last = await Base.json(this.config.cartUrl);
        if (!last.response.ok || fingerprint(raw) !== fingerprint(validateCart(last.data, this.config.currency))) throw Error('cart-changed');
        return { ...initial, data: expanded };
      }
      giftRules(cart) {
        const rules = [...document.querySelectorAll('k9-gift-cart')].flatMap(e => e.rules || []);
        const owned = rules.filter(r => cart.items.some(i => i.properties?._k9_cadeau === r.handle));
        if (cart.items.some(i => i.properties?._k9_cadeau && !owned.some(r => r.handle === i.properties._k9_cadeau))) throw Error('gift-rule');
        return owned.map(r => window.K9NativeGift.validateRule(r));
      }
      async quote(input, discountCodes, rules = [], isCurrent = () => true) {
        const request = quoteQueue.then(() => isCurrent()
          ? BogoOffer.prototype.performQuote.call(this, input, discountCodes, rules) : null);
        quoteQueue = request.catch(() => {});
        return request;
      }
      async performQuote(input, discountCodes, rules = []) {
        if (quoteUncertain || pendingQuotes.size) throw Error('quote-cleanup');
        if (!input.length) return { total: 0, discount: 0, rate: null, financial: financial({ items: [], total_price: 0, currency: this.config.currency }, this.config, rules) };
        const lines = quoteLines(input), url = new URL(this.config.storefrontUrl, location.origin);
        if (url.origin !== location.origin) throw Error('endpoint');
        let created;
        try {
          quoteUncertain = true;
          const { response, data } = await Base.json(url.href, { method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query, variables: { country: this.config.country,
              input: { buyerIdentity: { countryCode: this.config.country }, lines, discountCodes } } }) });
          const result = data.data?.cartCreate, cart = result?.cart;
          if (typeof cart?.id === 'string' && cart.id && Array.isArray(cart.lines?.nodes)
            && cart.lines.nodes.every(line => typeof line.id === 'string' && line.id)) {
            created = { id: cart.id, lines: cart.lines.nodes.map(line => line.id) };
            pendingQuotes.set(created.id, created.lines);
            quoteUncertain = false;
          } else if (cart === null && Array.isArray(result.userErrors) && result.userErrors.length) quoteUncertain = false;
          if (!created) throw Error('quote');
          if (!response.ok || data.errors?.length || !cart || cart.lines.pageInfo.hasNextPage
            || result.userErrors.length || result.warnings.length || cart.discountCodes.some(d => !d.applicable)
            || JSON.stringify(sorted(cart.discountCodes.map(d => d.code.toLowerCase())))
              !== JSON.stringify(sorted(discountCodes.map(code => code.toLowerCase())))) throw Error('quote');
          const actual = cart.lines.nodes.map(line => ({ id: Number(line.merchandise.id.split('/').pop()), quantity: line.quantity,
            properties: Object.fromEntries(line.attributes.map(a => [a.key, a.value])) }));
          const expected = quantities(input), received = quantities(actual);
          if (expected.size !== received.size || [...expected].some(([key, qty]) => received.get(key) !== qty)) throw Error('quote-shape');
          let discount = 0;
          const rates = new Set();
          for (const line of cart.lines.nodes) {
            const productId = Number(line.merchandise.product.id.split('/').pop());
            if (this.config.triggerVariants && productId === this.config.triggerProductId) {
              const carrier = triggerVariant(this.config, line.merchandise.id.split('/').pop());
              if (carrier && money(line.cost.subtotalAmount, this.config.currency) !== carrier.price * line.quantity) throw Error('price-changed');
            }
            if (!this.config.catalog.some(v => v.productId === productId)) continue;
            const variant = this.config.catalog.find(v => v.id === Number(line.merchandise.id.split('/').pop()));
            const subtotal = money(line.cost.subtotalAmount, this.config.currency);
            if (!variant || subtotal !== variant.price * line.quantity) throw Error('price-changed');
            // The rate is read on catalogue lines only: one automatic discount per line (otherwise « stacked »).
            for (const group of ownAllocations(line)) { discount += group.amount; rates.add(group.rate); }
          }
          if (rates.size > 1) throw Error('rate-mismatch');
          const [rate = null] = rates, units = discountedUnits(this.config, input);
          if (rate === null && units.length) throw Error('discount-absent');
          if (discount !== (rate === null ? 0 : saving(units, rate))) throw Error('expected-discount-missing');
          const quoted = { items: cart.lines.nodes.map(line => quotedLine(line, this.config.currency)),
            total_price: money(cart.cost.totalAmount, this.config.currency), currency: this.config.currency };
          return { total: quoted.total_price, discount, rate, financial: financial(quoted, this.config, rules) };
        } finally {
          if (created) {
            const { response, data } = await Base.json(url.href, { method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ query: clearQuery, variables: { country: this.config.country, cart: created.id, lines: created.lines } }) });
            const cleared = data.data?.cartLinesRemove;
            if (!response.ok || data.errors?.length || !Array.isArray(cleared?.userErrors)
              || cleared.userErrors.length || cleared.cart?.totalQuantity !== 0) throw Error('quote-cleanup');
            pendingQuotes.delete(created.id);
          }
        }
      }
      async proposal(cart, { choices = this.draft.choices, isCurrent = () => true } = {}) {
        if (!isCurrent()) return null;
        const added = items(this.config, this.mode, choices);
        const carrierOrLeash = cart.items.some(item => item.product_id === this.config.triggerProductId
          || this.config.catalog.some(v => v.productId === item.product_id));
        const eligible = isCollectionUpsell(this.config)
          ? qualifyingCount(this.config, cart.items) > 0 && !discountedUnits(this.config, cart.items).length
          : isShocksafe(this.config) && this.mode === 'bundle' ? !carrierOrLeash
          : isUpsell(this.config)
          ? cart.items.some(item => item.product_id === this.config.triggerProductId && this.config.triggerVariantIds.includes(item.id))
            && !cart.items.some(item => this.config.catalog.some(v => v.productId === item.product_id))
          : count(cart, this.config) % 2 === 1;
        if (!added || ((this.mode === 'cart' || isShocksafe(this.config)) && !eligible)) throw Error('eligibility');
        const rules = this.giftRules(cart);
        if (rules.length) await window.K9NativeGift.verifyCart(cart.raw || cart, rules);
        if (!isCurrent()) return null;
        const before = await this.quote(cart.items, codes(cart), rules, isCurrent);
        if (!isCurrent()) return null;
        if (before.total !== cart.total_price || before.financial !== financial(cart, this.config, rules)) throw Error('baseline-price');
        const after = await this.quote([...cart.items, ...added], codes(cart), rules, isCurrent);
        if (!isCurrent()) return null;
        // Lot 6b-1: one rate p, read on the quote (« after »), the same as an already formed pair (« before »).
        if (after.rate == null) throw Error('discount-absent');
        if (before.rate != null && before.rate !== after.rate) throw Error('rate-mismatch');
        const rate = after.rate, units = input => discountedUnits(this.config, input);
        const priceOf = id => (this.config.catalog.find(v => v.id === id) || triggerVariant(this.config, id)).price;
        const original = added.reduce((sum, item) => sum + priceOf(item.id) * item.quantity, 0);
        // Exact equality for every family: nothing else in the cart may move in price.
        const extra = saving(units([...cart.items, ...added]), rate) - saving(units(cart.items), rate);
        const delta = after.total - before.total;
        if (after.discount - before.discount !== extra || delta !== original - extra) throw Error('discount-not-confirmed');
        const leash = isUpsell(this.config) && added.find(item => this.config.catalog.some(v => v.id === item.id));
        const secondaryOriginal = leash ? priceOf(leash.id) : 0;
        return { delta, original, rate, total: after.total, financial: after.financial, rules,
          secondaryOriginal, secondaryTotal: leash ? secondaryOriginal - extra : 0,
          fingerprint: fingerprint(cart), displayFingerprint: displayFingerprint(cart) };
      }
      async refreshQuote() {
        if (this.preparationActive === false) return;
        const generation = this.generation, selection = this.selection, choices = { ...this.draft.choices };
        const revision = this.quoteRevision = (this.quoteRevision || 0) + 1;
        const isCurrent = () => this.isConnected && generation === this.generation
          && selection === this.selection && revision === this.quoteRevision;
        try {
          const current = await this.readCart();
          if (!isCurrent()) return;
          if (!current.response.ok) throw Error('cart');
          const cart = validateCart(current.data, this.config.currency);
          if (this.matchHarnessColor(cart)) { this.update(); return; }
          const quote = await this.proposal(cart, { choices, isCurrent });
          if (!isCurrent()) return;
          // Lot 6b-1: no rate without a successful quote.
          if (!Number.isInteger(quote.rate) || quote.rate < 1 || quote.rate > 99) throw Error('rate-invalid');
          this.verified = quote;
          const format = cents => new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency }).format(cents / 100);
          this.querySelector('[data-total]').textContent = format(quote.delta);
          this.querySelector('[data-original]').textContent = format(quote.original);
          // Lot 6 (owner, 27 Oct.): the rate is shown on the leash alone, with its own before and after prices.
          for (const [selector, value] of [['[data-secondary-total]', quote.secondaryTotal], ['[data-secondary-original]', quote.secondaryOriginal]]) {
            const field = this.querySelector(selector);
            if (field && Number.isSafeInteger(value)) field.textContent = format(value);
          }
          // Every rate site keeps its template: rewritten at each quote, never from the text already shown.
          for (const site of this.querySelectorAll('[data-rate-template]')) {
            site.textContent = site.getAttribute('data-rate-template').replaceAll('{rate}', String(quote.rate));
          }
          delete this.dataset.k9Devis; delete this.dataset.k9Raison; this.hidden = false; this.update();
        } catch (error) {
          if (isCurrent()) { this.verified = null; this.dataset.k9Devis = 'echec'; this.dataset.k9Raison = reason(error); this.hidden = true; }
        }
      }
      async submit() {
        if (state.pending || this.draft.blocked || !this.verified || Base.cartIsBusy() || window.K9NativeGift?.checkoutBlocked) return;
        const added = items(this.config, this.mode, this.draft.choices); if (!added) return;
        const visible = this.verified, generation = this.generation, selection = JSON.stringify(this.draft.choices);
        state.pending = true; state.version++; refresh();
        let posted = false, success = false, after;
        try {
          const baseline = await this.readCart(); if (!baseline.response.ok) throw Error('baseline');
          validateCart(baseline.data, this.config.currency);
          const quote = await this.proposal(baseline.data);
          if (quote.delta !== visible.delta || quote.rate !== visible.rate
            || (quote.displayFingerprint ?? quote.fingerprint) !== (visible.displayFingerprint ?? visible.fingerprint)) throw Error('cart-changed');
          const last = await this.readCart();
          if (!last.response.ok || fingerprint(validateCart(last.data, this.config.currency)) !== quote.fingerprint
            || !this.isConnected || this.generation !== generation || JSON.stringify(this.draft.choices) !== selection
            || Base.cartIsBusy() || window.K9NativeGift?.checkoutBlocked) throw Error('cart-changed');
          posted = true; window.K9NativeGift?.holdMutation(true);
          const result = await Base.json(this.config.addUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: added }) });
          const current = await this.readCart(); if (!current.response.ok) throw Error('cart');
          after = validateCart(current.data, this.config.currency);
          success = confirmed(baseline.data, after, added, quote, this.config, quote.rules);
          if (success && quote.rules.length) {
            await window.K9NativeGift.verifyCart(after.raw || after, quote.rules);
            const reference = fingerprint(after), latest = await this.readCart();
            if (!latest.response.ok || fingerprint(validateCart(latest.data, this.config.currency)) !== reference) throw Error('cart-changed');
          }
          if (!success) {
            const untouched = fingerprint(after) === fingerprint(baseline.data);
            this.draft.blocked = result.response.ok || !untouched;
            this.draft.message = this.config.text[this.draft.blocked ? 'uncertain' : 'rejected'];
          } else {
            this.draft.message = '';
            // Lot 4: anonymous acceptance count (k9-offre-mesure.js), only after full confirmation.
            (window.K9MesureFile ||= []).push({ element: this, total: baseline.data.total_price, currency: baseline.data.currency });
          }
        } catch {
          success = false;
          this.draft.blocked = posted; this.draft.message = this.config.text[posted ? 'uncertain' : 'beforeError'];
          if (posted) {
            try { const current = await this.readCart(); if (current.response.ok) after = validateCart(current.data, this.config.currency); }
            catch { /* Never retry, remove or restore paid items automatically. */ }
          }
        }
        try {
          if (posted && after && document.querySelector('cart-items')) { location.reload(); return; }
          // Brief confirmation on the button, then the redraw removes the accepted offer.
          if (success && after && isShocksafe(this.config) && this.config.text.addedButton) {
            this.addedShown = true; this.button.classList.add('is-added'); this.update();
            await new Promise(resolve => setTimeout(resolve, 900));
          }
          const rendered = after && await this.redraw(after, success ? added.at(-1).id : null);
          if (!rendered && success) this.draft.message = this.config.text.added;
          const current = offers().find(e => e.key === this.key);
          current?.message(this.draft.message || '', this.draft.blocked || (success && !rendered));
        } catch { this.message(success ? this.config.text.added : this.draft.message, true); }
        finally {
          if (this.addedShown) { this.addedShown = false; this.button.classList.remove('is-added'); }
          state.pending = false; state.version++; refresh();
          if (!posted && this.isConnected) { this.verified = null; void this.refreshQuote(); }
          // Carrier page (decision 7): once its carrier is in the cart, the offer re-quotes and leaves the page.
          else if (success && isShocksafe(this.config) && this.mode === 'bundle' && this.isConnected) { this.selection = ''; this.update(); }
        }
      }
    }
    customElements.define('k9-bogo-offer', BogoOffer);
  }
  register(); document.addEventListener('DOMContentLoaded', register, { once: true });
})();
